import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { hideSpinnersStyle, ITEM_TYPES, OBJECT_TYPES } from './constants';
import { 
  ChevronDown, ChevronRight, MapPin, Ruler, Phone, Mail, 
  Calendar, FileText, Image, Download, Lock, ExternalLink,
  Building2, CheckCircle2, MessageCircle
} from 'lucide-react';
import ChatModal from './ChatModal';

const ClientView = ({ shareToken }) => {
  // Визначаємо чи колір темний — для авто-контрасту тексту
  const isDark = (hex) => {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 140;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedEstimates, setExpandedEstimates] = useState({});
  const [showChat, setShowChat] = useState(false);

  // Завантаження даних
  useEffect(() => {
    loadSharedProject();
  }, [shareToken]);

  const loadSharedProject = async () => {
    try {
      setLoading(true);
      
      // 1. Отримуємо share запис
      const { data: share, error: shareError } = await supabase
        .from('shared_projects')
        .select('*')
        .eq('share_token', shareToken)
        .single();
      
      if (shareError || !share) {
        setError('Посилання недійсне або проєкт не знайдено');
        setLoading(false);
        return;
      }
      
      setShareData(share);
      
      // Перевірка чи посилання активне
      if (share.is_active === false) {
        setError('inactive');
        setLoading(false);
        return;
      }
      
      // 2. Якщо locked — показуємо snapshot
      if (share.is_locked && share.locked_snapshot) {
        setProject(share.locked_snapshot);
        setLoading(false);
        return;
      }
      
      // 3. Якщо unlocked — завантажуємо live дані
      const { data: projectRow, error: projectError } = await supabase
        .from('projects')
        .select('data')
        .eq('user_id', share.user_id)
        .eq('data->>id', String(share.project_id));
      
      if (projectError || !projectRow || projectRow.length === 0) {
        // Пробуємо альтернативний спосіб
        const { data: allProjects, error: allError } = await supabase
          .from('projects')
          .select('data')
          .eq('user_id', share.user_id);
        
        if (allError || !allProjects) {
          setError('Проєкт не знайдено');
          setLoading(false);
          return;
        }
        
        const found = allProjects.find(row => String(row.data?.id) === String(share.project_id));
        if (found) {
          setProject(found.data);
        } else {
          setError('Проєкт не знайдено');
        }
      } else {
        setProject(projectRow[0].data);
      }
      
      setLoading(false);
    } catch (e) {
      console.error('Помилка завантаження:', e);
      setError('Помилка завантаження даних');
      setLoading(false);
    }
  };

  const toggleSection = (estimateId, sectionId) => {
    const key = `${estimateId}-${sectionId}`;
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleEstimate = (estimateId) => {
    setExpandedEstimates(prev => ({ ...prev, [estimateId]: !prev[estimateId] }));
  };

  // Розрахунок підсумків кошторису
  const calcEstimateTotals = (estimate) => {
    let totalClientCost = 0;
    let atsAmount = 0;
    const atsEnabled = estimate.atsEnabled ?? true;
    const atsPercent = estimate.atsPercent ?? 5;

    (estimate.items || []).forEach(section => {
      let sectionClientCost = 0;
      (section.items || []).forEach(item => {
        if (item.hidden) return;
        const cp = item.clientPrice !== undefined 
          ? item.clientPrice 
          : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
        sectionClientCost += Math.round((item.qty || 0) * cp * 100) / 100;
      });
      totalClientCost += sectionClientCost;
      if (atsEnabled && (section.includeATS ?? true)) {
        atsAmount += sectionClientCost * atsPercent / 100;
      }
    });

    return {
      totalClientCost: Math.round(totalClientCost * 100) / 100,
      atsAmount: Math.round(atsAmount * 100) / 100,
      grandTotal: Math.round((totalClientCost + atsAmount) * 100) / 100
    };
  };

  // Розрахунок підсумків секції
  const calcSectionTotals = (section) => {
    let clientCost = 0;
    (section.items || []).forEach(item => {
      if (item.hidden) return;
      const cp = item.clientPrice !== undefined 
        ? item.clientPrice 
        : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
      clientCost += Math.round((item.qty || 0) * cp * 100) / 100;
    });
    return { clientCost: Math.round(clientCost * 100) / 100 };
  };

  // Форматування оплат
  const getEstimatePayments = (estimate) => {
    return (estimate.payments || []).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getEstimateReceived = (estimate) => {
    return (estimate.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  // --- РЕНДЕР ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-gray-400" />
          </div>
          {error === 'inactive' ? (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Доступ до проєкту закрито</h1>
              <p className="text-sm text-gray-500">Власник проєкту тимчасово обмежив доступ до перегляду. Зверніться до менеджера проєкту для отримання актуального посилання.</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-gray-900 mb-2">Посилання недійсне</h1>
              <p className="text-sm text-gray-500">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!project) return null;

  const objectType = OBJECT_TYPES[project.objectType] || OBJECT_TYPES.other;
  const showPreliminary = shareData?.show_preliminary ?? false;
  
  // Фільтруємо кошториси — показуємо звіти, попередній тільки якщо дозволено
  const visibleEstimates = (project.estimates || []).filter(est => 
    est.isPreliminary ? showPreliminary : true
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{hideSpinnersStyle}</style>
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="logo-text text-lg">LIHTAR</span>
            <span className="text-xs text-gray-400 hidden sm:inline">• кошторис проєкту</span>
          </div>
          {shareData?.is_locked && (
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Зафіксовано
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6 space-y-4">
        
        {/* Брендинг-блок */}
        {shareData?.brand_settings && (shareData.brand_settings.logo || shareData.brand_settings.companyName) && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 sm:py-5">
              <div className="flex items-start gap-4">
                {/* Логотип по центру */}
                {shareData.brand_settings.logo && (
                  <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 120 }}>
                    <img src={shareData.brand_settings.logo} alt="" className="max-h-14 max-w-[120px] object-contain" />
                  </div>
                )}
                {/* Контакти по правому краю */}
                <div className="flex-1 text-right">
                  {shareData.brand_settings.companyName && (
                    <div className="text-sm sm:text-base font-semibold" style={{ color: '#163248' }}>{shareData.brand_settings.companyName}</div>
                  )}
                  {shareData.brand_settings.managerName && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-0.5">{shareData.brand_settings.managerName}</div>
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm text-gray-500">
                    {shareData.brand_settings.phone && (
                      <a href={`tel:${shareData.brand_settings.phone}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Phone className="w-3 h-3" />{shareData.brand_settings.phone}
                      </a>
                    )}
                    {shareData.brand_settings.email && (
                      <a href={`mailto:${shareData.brand_settings.email}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Mail className="w-3 h-3" />{shareData.brand_settings.email}
                      </a>
                    )}
                    {shareData.brand_settings.website && (
                      <a href={shareData.brand_settings.website.startsWith('http') ? shareData.brand_settings.website : `https://${shareData.brand_settings.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-gray-600">
                        <ExternalLink className="w-3 h-3" />{shareData.brand_settings.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Картка проєкту */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#f0f4f8' }}>
                {objectType.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl font-bold" style={{ color: '#163248' }}>
                  {project.name || 'Без назви'}
                </h1>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                  {project.address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {project.address}
                    </span>
                  )}
                  {project.area > 0 && (
                    <span className="flex items-center gap-1">
                      <Ruler className="w-3.5 h-3.5" />
                      {project.area} м²
                    </span>
                  )}
                </div>
                {/* Контакти менеджера */}
                {(project.designerName || project.designerPhone) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                    {project.designerName && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {project.designerName}
                      </span>
                    )}
                    {project.designerPhone && (
                      <a href={`tel:${project.designerPhone}`} className="flex items-center gap-1 text-blue-600">
                        <Phone className="w-3.5 h-3.5" />
                        {project.designerPhone}
                      </a>
                    )}
                    {project.designerEmail && (
                      <a href={`mailto:${project.designerEmail}`} className="flex items-center gap-1 text-blue-600">
                        <Mail className="w-3.5 h-3.5" />
                        {project.designerEmail}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Кошториси / Звіти */}
        {visibleEstimates.map((estimate) => {
          const totals = calcEstimateTotals(estimate);
          const isOpen = expandedEstimates[estimate.id] !== false; // відкриті за замовч
          const payments = getEstimatePayments(estimate);
          const received = getEstimateReceived(estimate);
          const atsEnabled = estimate.atsEnabled ?? true;
          const atsPercent = estimate.atsPercent ?? 5;

          return (
            <div key={estimate.id} className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {/* Шапка кошторису */}
              <div 
                className="px-4 sm:px-6 py-3 sm:py-4 cursor-pointer flex items-center justify-between"
                style={{ backgroundColor: estimate.isPreliminary ? '#163248' : 'white' }}
                onClick={() => toggleEstimate(estimate.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen 
                    ? <ChevronDown className={`w-4 h-4 flex-shrink-0 ${estimate.isPreliminary ? 'text-white/60' : 'text-gray-400'}`} />
                    : <ChevronRight className={`w-4 h-4 flex-shrink-0 ${estimate.isPreliminary ? 'text-white/60' : 'text-gray-400'}`} />
                  }
                  <h2 className={`font-semibold truncate ${estimate.isPreliminary ? 'text-white' : ''}`} style={!estimate.isPreliminary ? { color: '#163248' } : {}}>
                    {estimate.isPreliminary ? 'Попередній кошторис' : estimate.name}
                  </h2>
                </div>
                <span className={`font-bold tabular-nums flex-shrink-0 ml-2 ${estimate.isPreliminary ? 'text-white' : ''}`} style={!estimate.isPreliminary ? { color: '#163248' } : {}}>
                  {totals.grandTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                </span>
              </div>

              {isOpen && (
                <div>
                  {/* Розділи */}
                  {(estimate.items || []).map((section, sIdx) => {
                    const st = calcSectionTotals(section);
                    const sectionKey = `${estimate.id}-${section.id}`;
                    const isSectionOpen = expandedSections[sectionKey] !== false;
                    const visibleItems = (section.items || []).filter(item => !item.hidden);

                    return (
                      <div key={section.id} className="border-t">
                        {/* Шапка розділу */}
                        <div 
                          className="flex items-center justify-between px-4 sm:px-6 py-2.5 cursor-pointer active:bg-gray-100"
                          style={{ backgroundColor: shareData?.brand_settings?.sectionBg || '#fde68a' }}
                          onClick={() => toggleSection(estimate.id, section.id)}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isSectionOpen 
                              ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#9ca3af' }} />
                              : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#9ca3af' }} />
                            }
                            <span className="text-sm font-medium" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? '#ffffff' : '#163248', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {sIdx + 1}. {section.name}
                            </span>
                            <span className="text-xs" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? 'rgba(255,255,255,0.6)' : '#9ca3af' }}>({visibleItems.length})</span>
                            {atsEnabled && (
                              <span className={`text-[9px] font-bold px-1 py-0.5 rounded flex-shrink-0 ${
                                (section.includeATS ?? true) ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'
                              }`}>
                                АТС
                              </span>
                            )}
                          </div>
                          <span className="text-sm font-semibold tabular-nums flex-shrink-0" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? '#ffffff' : '#163248' }}>
                            {st.clientCost.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                          </span>
                        </div>

                        {/* Позиції */}
                        {isSectionOpen && visibleItems.length > 0 && (
                          <div className="divide-y divide-gray-100">
                            {visibleItems.map((item) => {
                              const cp = item.clientPrice !== undefined 
                                ? item.clientPrice 
                                : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                              const total = Math.round((cp || 0) * (item.qty || 0) * 100) / 100;
                              const typeConfig = ITEM_TYPES[item.type] || ITEM_TYPES['Робота'];

                              return (
                                <div key={item.id} className={`flex items-center px-4 sm:px-6 py-2 ${item.completed ? 'bg-blue-50/30' : ''}`}>
                                  {/* Completed */}
                                  {item.completed && (
                                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mr-2" />
                                  )}
                                  {/* Type badge */}
                                  <span 
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mr-2"
                                    style={{ backgroundColor: {'Робота':'#22c55e','Матеріали':'#eab308','Комплектація':'#3b82f6','Транспорт':'#f97316','Інші послуги':'#a855f7'}[item.type] || '#9ca3af' }}
                                  >
                                    {typeConfig.letter}
                                  </span>
                                  {/* Назва + деталі */}
                                  <div className={`flex-1 min-w-0 mr-2 ${item.completed ? 'opacity-50' : ''}`}>
                                    <p className={`text-sm ${item.completed ? 'line-through' : ''}`} style={{ color: '#163248', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                      {item.position || 'Без назви'}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-gray-400">{item.qty || 0} {item.unit}</span>
                                      <span className="text-xs text-gray-400">× {cp.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                                    </div>
                                  </div>
                                  {/* Сума */}
                                  <span className="text-sm tabular-nums flex-shrink-0" style={{ color: '#163248' }}>
                                    {total.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Підсумки кошторису */}
                  <div className="border-t px-4 sm:px-6 py-3 space-y-1.5" style={{ backgroundColor: '#f9fafb' }}>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Вартість робіт/матеріалів:</span>
                      <span className="font-medium">{totals.totalClientCost.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                    </div>
                    {atsEnabled && totals.atsAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span style={{ color: shareData?.brand_settings?.primaryColor || '#d97706' }}>Адміністративно-технічний супровід ({atsPercent}%):</span>
                        <span className="font-medium" style={{ color: shareData?.brand_settings?.primaryColor || '#d97706' }}>{totals.atsAmount.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between px-4 sm:px-6 py-3" style={{ backgroundColor: shareData?.brand_settings?.sectionBg || '#fde68a' }}>
                    <span className="font-semibold" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? '#ffffff' : (shareData?.brand_settings?.primaryColor || '#163248') }}>Разом:</span>
                    <span className="font-bold text-lg" style={{ color: isDark(shareData?.brand_settings?.sectionBg) ? '#ffffff' : (shareData?.brand_settings?.primaryColor || '#163248') }}>
                      {totals.grandTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                    </span>
                  </div>

                  {/* Документи */}
                  {(estimate.documents || []).filter(d => d.visible !== false).length > 0 && (
                    <div className="border-t px-4 sm:px-6 py-3">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Документи</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {(estimate.documents || []).filter(d => d.visible !== false).map((doc, dIdx) => (
                          <a
                            key={doc.id || dIdx}
                            href={doc.webViewLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50 transition-colors text-sm"
                          >
                            {doc.type?.startsWith('image/') ? (
                              <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                            )}
                            <span className="truncate text-xs text-gray-700">{doc.name}</span>
                            <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0 ml-auto" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* === Загальний прогрес === */}
        {(() => {
          // Збираємо статистику з попереднього кошторису
          const preliminary = (project.estimates || []).find(e => e.isPreliminary);
          const allItems = preliminary ? (preliminary.items || []).flatMap(s => s.items || []) : [];
          const totalItems = allItems.length;
          const completedItems = allItems.filter(i => i.completed).length;
          const workPercent = totalItems > 0 ? Math.round(completedItems / totalItems * 100) : 0;

          // Загальні суми по всіх звітах
          const reports = (project.estimates || []).filter(e => !e.isPreliminary);
          let totalExpenses = 0;
          let totalReceived = 0;
          let allPayments = [];

          reports.forEach(est => {
            const t = calcEstimateTotals(est);
            totalExpenses += t.grandTotal;
            totalReceived += getEstimateReceived(est);
            (est.payments || []).forEach(p => {
              allPayments.push({ ...p, estimateName: est.name });
            });
          });

          allPayments.sort((a, b) => new Date(a.date) - new Date(b.date));

          const balance = totalReceived - totalExpenses;
          const budgetPreliminary = preliminary ? calcEstimateTotals(preliminary).grandTotal : 0;
          const budgetPercent = budgetPreliminary > 0 ? Math.round(totalExpenses / budgetPreliminary * 100) : 0;
          const budgetColor = budgetPercent > 100 ? '#dc2626' : budgetPercent > 80 ? '#f59e0b' : '#22c55e';
          const area = project.area || 0;

          return (
            <>
              {/* Прогрес */}
              {(totalItems > 0 || totalExpenses > 0) && (
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden px-4 sm:px-6 py-4 space-y-4">
                  <h3 className="text-sm font-semibold" style={{ color: '#163248' }}>Прогрес проєкту</h3>
                  
                  {totalItems > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-600">Виконання робіт</span>
                        <span className="text-xs font-semibold text-blue-500">{completedItems} з {totalItems} ({workPercent}%)</span>
                      </div>
                      <div className="h-2.5 rounded-full overflow-hidden bg-gray-200">
                        <div className="h-full rounded-full transition-all duration-500 bg-blue-500" style={{ width: `${Math.min(workPercent, 100)}%` }} />
                      </div>
                    </div>
                  )}

                  {budgetPreliminary > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-600">Бюджет</span>
                        <span className="text-xs font-semibold" style={{ color: budgetColor }}>
                          {totalExpenses.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} з {budgetPreliminary.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} грн ({budgetPercent}%)
                        </span>
                      </div>
                      <div className="h-2.5 rounded-full overflow-hidden bg-gray-200">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(budgetPercent, 100)}%`, backgroundColor: budgetColor }} />
                      </div>
                    </div>
                  )}

                  {area > 0 && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs font-medium text-gray-600">Вартість за м²</span>
                      <div className="flex items-center gap-3">
                        {budgetPreliminary > 0 && (
                          <span className="text-xs text-gray-500">
                            План: <span className="font-semibold" style={{ color: '#163248' }}>{Math.round(budgetPreliminary / area).toLocaleString('uk-UA')} ₴</span>
                          </span>
                        )}
                        {totalExpenses > 0 && (
                          <>
                            {budgetPreliminary > 0 && <span className="text-xs text-gray-300">→</span>}
                            <span className="text-xs text-gray-500">
                              Факт: <span className="font-semibold" style={{ color: totalExpenses / area > budgetPreliminary / area ? '#dc2626' : '#22c55e' }}>
                                {Math.round(totalExpenses / area).toLocaleString('uk-UA')} ₴
                              </span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Оплати загальні */}
              {allPayments.length > 0 && (
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden px-4 sm:px-6 py-4">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: '#163248' }}>Оплати</h3>
                  <div className="space-y-1.5">
                    {allPayments.map((payment, pIdx) => (
                      <div key={payment.id || pIdx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                            {new Date(payment.date).toLocaleDateString('uk-UA')}
                          </span>
                          <span className="text-xs text-gray-400 truncate">{payment.estimateName}</span>
                          {payment.note && <span className="text-gray-500 truncate hidden sm:inline">— {payment.note}</span>}
                        </div>
                        <span className="font-medium text-green-600 tabular-nums flex-shrink-0 ml-2">
                          +{(payment.amount || 0).toLocaleString('uk-UA')} ₴
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Підсумок */}
                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Загальні витрати:</span>
                      <span className="font-semibold" style={{ color: '#163248' }}>{totalExpenses.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Оплачено:</span>
                      <span className="font-semibold text-green-600">{totalReceived.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1.5 border-t border-gray-100">
                      <span className="font-medium" style={{ color: '#163248' }}>{balance >= 0 ? 'Переплата:' : 'Залишок до сплати:'}</span>
                      <span className={`font-bold text-lg ${balance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {Math.abs(balance).toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* FAB кнопка чату */}
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-5 right-5 w-14 h-14 rounded-full shadow-lg text-white flex items-center justify-center z-20 active:scale-95 transition-transform"
          style={{ backgroundColor: '#163248' }}
        >
          <MessageCircle className="w-6 h-6" />
        </button>

        {/* Чат модалка */}
        <ChatModal
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          projectId={project?.id}
          isClientMode={true}
          shareToken={shareToken}
        />

        {/* Footer */}
        <div className="text-center py-6 text-xs text-gray-400">
          <span className="logo-text text-sm">LIHTAR</span>
          <span className="ml-1">• Управління бюджетом будівництва</span>
        </div>
      </div>
    </div>
  );
};

export default ClientView;
